import './UsersListItem.sass';
import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  useCurrentUserInfo,
  useDesktop,
  useProfileInfo,
  useTranslator,
  useUnitName,
  useUserInfo,
} from '$hooks';
import { RootState } from '$store/rootReducer';
import { profileActions } from '$store/profile';
import copyToClipboard from 'copy-text-to-clipboard';
import { Button, CellButton, Collapse, List, ListItem, Tabs, TabsItem } from '$uikit';
import { classNames } from '$utils';
import { Truncate, UnitName, UnitPhoto } from '$shared/components';
import { CHAT } from '$shared/constants/pages';
import {
  IcCopy28,
  IcEdit28,
  IcList28,
  IcMessage28,
  IcMore24,
  IcMore28,
  IcUserPlus28,
  IcWrite28,
} from '$assets';
import router from '$navigation/router';
import { BottomSheet } from '$uikit/BottomSheet/BottomSheet';
import { UsersListItemProps, UsersContentListItem } from './UsersListItem.interface';
import { noteSelector } from '$store/notes';
import { PopupEditNote } from '$core/UserInfo/PopupEditNote/PopupEditNote';
import { PopupEditName } from '$core/UserInfo/PopupEditName/PopupEditName';
import { showModal } from '$navigation/helper';

export const UsersListItemComponent = ({
  userId,
  data: { userData, collapseData, contentData },
  isCollapse,
  isShowMoreButton,
  listId,
}: UsersListItemProps) => {
  const dispatch = useDispatch();
  const t = useTranslator();
  const isDesktop = useDesktop();
  const [isCollapseOpen, setIsCollapseOpen] = useState(false);
  const [selectedCollapseTab, setSelectedCollapseTab] = useState(
    collapseData?.tabs[0].tabName || '',
  );
  const [isOpenMoreMenu, setOpenMoreMenu] = useState(false);
  const [isBottomSheetOpen, setIsBottomSheetOpen] = useState(false);
  const [isOpenBottomSheetEditNote, setOpenBottomSheetEditNote] = useState(false);
  const [isOpenBottomSheetEditName, setOpenBottomSheetEditName] = useState(false);
  const { isFollowFromMe } = useProfileInfo(userId);
  const userNote = useSelector((state: RootState) => noteSelector(state, userId));
  const name = useUnitName(userData);
  const myUser = useCurrentUserInfo();
  const editUser = useUserInfo(userId);

  const handleEditName = (e) => {
    if (!(myUser?.verified === 2 || editUser?.verified !== 2)) return;
    e.preventDefault();
    e.stopPropagation();
    setOpenMoreMenu(false);
    setOpenBottomSheetEditName(!isOpenBottomSheetEditName);
  };

  const handleEditNote = () => {
    if (!(myUser?.verified === 2 || editUser?.verified !== 2)) return;
    setOpenMoreMenu(false);
    setOpenBottomSheetEditNote(!isOpenBottomSheetEditNote);
  };

  const handleFollow = () => {
    dispatch(profileActions.follow({ id: userId }));
  };

  const copyLinkToProfile = () => {
    copyToClipboard(
      `https://${location.host}/${
        userData.domain ? userData.domain : `id${userData.id}`
      }`,
    );
  };

  const getListItem = (listItem: UsersContentListItem) => {
    const { title, subTitle, isHighlightedTitle, isHighlightedLabel } = listItem;
    return (
      <ListItem
        key={title}
        indicator={subTitle}
        descriptionSingleLine
        className={classNames({
          _isHighlightedTitle: isHighlightedTitle,
          _isHighlightedLabel: isHighlightedLabel,
        })}
      >
        {title}
      </ListItem>
    );
  };

  function renderBottomSheetEditHeader() {
    if (!userData) return <></>;

    return (
      <div className="UsersListItem__BottomSheet__header">
        <UnitPhoto unit={userData} />
        <div className="UsersListItem__BottomSheet__header__name">
          <div>
            <UnitName unit={userData} />
          </div>
          {renderUserDomain()}
        </div>
      </div>
    );
  }

  function renderUserDomain() {
    return (
      <div className="UsersListItem__domain">
        {userData.domain ? `@${userData.domain}` : `id${userData.id}`}
        &nbsp;
        <span className="UsersListItem__domain_bold">
          {Boolean(userNote?.name) && name}
        </span>
      </div>
    );
  }

  function renderListContent() {
    if (!contentData || !contentData.length) {
      return null;
    }

    return (
      <List className="UsersListItem__ListContent">{contentData.map(getListItem)}</List>
    );
  }

  function renderCollapse() {
    if (!collapseData) {
      return null;
    }

    const { title, subTitle, tabs } = collapseData;

    const tabsContent = (
      <>
        <Tabs selected={selectedCollapseTab} onChange={setSelectedCollapseTab}>
          {tabs.map((tab) => {
            const { tabName } = tab;
            return (
              <TabsItem key={tabName} value={tabName}>
                {tabName}
              </TabsItem>
            );
          })}
        </Tabs>
        <List>
          {tabs
            .find((tab) => tab.tabName === selectedCollapseTab)
            ?.tabContent.map(getListItem)}
        </List>
      </>
    );

    if (!isCollapse) {
      return tabsContent;
    }

    return (
      <Collapse
        title={title}
        onClick={() => setIsCollapseOpen(!isCollapseOpen)}
        isOpen={isCollapseOpen}
        subTitle={subTitle}
        className="UsersListItem__Collapse"
        isReversed
        hideTitleOnOpen
      >
        {tabsContent}
      </Collapse>
    );
  }

  function renderHeader() {
    return (
      <div className="UsersListItem__header">
        <div className="UsersListItem__header__container">
          <div className="UsersListItem__header_info">
            <UnitPhoto unit={userData} size="story" />
            <div className="UsersListItem__userInfo">
              <div className="UsersListItem__userInfo_name" onClick={handleEditName}>
                <span>
                  <UnitName unit={userData} />
                </span>
                {(myUser?.verified !== 2 || editUser?.verified !== 2) && (
                  <IcEdit28 className="UsersListItem__userInfo_name__button_edit UserListItem__icon_animation" />
                )}
              </div>
              {renderUserDomain()}
            </div>
          </div>
          {!isCollapse && editUser?.verified !== 2 && (
            <div
              className="UsersListItem__menu UserListItem__icon_animation"
              onClick={() => setOpenMoreMenu(true)}
            >
              <IcMore28 />
            </div>
          )}
        </div>
        {isShowMoreButton && (
          <div
            className="UsersListItem__header_btn"
            onClick={() => setIsBottomSheetOpen(!isBottomSheetOpen)}
          >
            <IcMore24 />
          </div>
        )}
      </div>
    );
  }

  function renderContent() {
    return (
      <div className="UsersListItem__actionButtons__container">
        {userNote && (
          <div className="UsersListItem__actionButtons__note">
            <Truncate
              lines={2}
              limit={isDesktop ? 78 : 71}
              expandText={t('read_more')}
              text={userNote.note}
            />
          </div>
        )}
        <div
          className={classNames({
            UsersListItem__actionButtons: true,
            hideColumns:
              !(!isFollowFromMe && !userData.isFollowFromMe) ||
              (myUser?.verified === 2 && editUser?.verified === 2) ||
              editUser?.verified === 2,
          })}
        >
          {(myUser?.verified !== 2 || editUser?.verified !== 2) && (
            <Button
              iconRight={<IcWrite28 width={16} height={16} />}
              mode="quinary"
              size="extra-small"
              onClick={handleEditNote}
            >
              {t('edit_note_button')}
            </Button>
          )}
          <Button
            iconRight={<IcMessage28 />}
            mode="quinary"
            size="extra-small"
            onClick={() => router.navigate(CHAT, { peerId: userId })}
          >
            {t('write_message_button')}
          </Button>
          {!isFollowFromMe && !userData.isFollowFromMe && (
            <Button
              iconRight={<IcUserPlus28 />}
              mode="quinary"
              size="extra-small"
              onClick={handleFollow}
            >
              {t('follow_button')}
            </Button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="UsersListItem">
      {renderHeader()}
      {renderContent()}
      {renderListContent()}
      {renderCollapse()}
      {isBottomSheetOpen && (
        <BottomSheet
          title={<UnitName unit={userData} />}
          onClose={() => setIsBottomSheetOpen(false)}
        >
          <CellButton onClick={copyLinkToProfile} icon={<IcCopy28 />}>
            {t('copy_link_to_profile')}
          </CellButton>
          <CellButton
            onClick={() => {
              setIsBottomSheetOpen(false);
              showModal(`member_lists${userData.id}`, false, {
                refresh: listId,
              });
            }}
            icon={<IcList28 />}
          >
            {t('lists_title')}
          </CellButton>
        </BottomSheet>
      )}
      {isOpenBottomSheetEditNote && (
        <BottomSheet
          title={renderBottomSheetEditHeader()}
          onClose={() => setOpenBottomSheetEditNote(false)}
        >
          <PopupEditNote
            onClose={() => setOpenBottomSheetEditNote(false)}
            userId={userId}
            maxNoteLength={200}
          />
        </BottomSheet>
      )}
      {isOpenBottomSheetEditName && (
        <BottomSheet
          title={renderBottomSheetEditHeader()}
          onClose={() => setOpenBottomSheetEditName(false)}
        >
          <PopupEditName
            onClose={() => setOpenBottomSheetEditName(false)}
            userId={userId}
            maxNoteLength={50}
            userName={name}
          />
        </BottomSheet>
      )}
      {isOpenMoreMenu && (
        <BottomSheet
          title={t('user_list_item_more_menu_header')}
          onClose={() => setOpenMoreMenu(false)}
        >
          <div className="UserListItem__menu__container">
            <List>
              <ListItem onClick={handleEditName}>
                {t('user_list_item_more_menu_edit_user_name')}
              </ListItem>
              <ListItem onClick={handleEditNote}>
                {t('user_list_item_more_menu_edit_user_note')}
              </ListItem>
            </List>
          </div>
        </BottomSheet>
      )}
    </div>
  );
};

export const UsersListItem = React.memo(UsersListItemComponent, (oldProps, newProps) => {
  return oldProps.userId === newProps.userId;
});
